import { DateTime } from 'luxon';
import { ofType } from 'redux-observable';
import { EMPTY, from, of } from 'rxjs';
import { catchError, filter, mergeMap } from 'rxjs/operators';

import { FeatureFlags } from '../../feature-flags/feature-flags';
import { FeatureFlagDeclarations } from '../../feature-flags/feature-flag-declarations';
import { NotificationHubApiService } from '../../services/notification-hub-api-service';

import { PERSIST_NOTIFICATION, addNotification } from './notification-hub.actions';
import { NotificationType } from './notification-hub.types';

const AUTO_TRIGGER_COOLDOWN_HOURS = 24;

/**
 * Notification types triggered by explicit user actions (batch, import, export, etc.).
 * These should always create new records - no cooldown deduplication.
 */
const USER_ACTION_NOTIFICATION_TYPES = new Set([
  NotificationType.BATCH_COMPLETED,
  NotificationType.BATCH_FAILED,
  NotificationType.ORDERS_IMPORTED,
  NotificationType.EXPORT_COMPLETED,
  NotificationType.INVENTORY_SYNCED,
  NotificationType.LABELS_CREATED,
  NotificationType.LABELS_PRINTED,
  NotificationType.STORE_CONNECTED,
  NotificationType.SETTINGS_SAVED,
  NotificationType.STORE_IMPORT_FAILED,
  NotificationType.CSV_IMPORT_COMPLETED,
  NotificationType.CSV_IMPORT_COMPLETED_WITH_WARNINGS,
  NotificationType.CSV_IMPORT_FAILED,
  NotificationType.CUSTOMERS_IMPORTED,
  NotificationType.CUSTOMERS_IMPORT_FAILED,
  NotificationType.ORDERS_EXPORT_STARTED,
  NotificationType.ORDERS_EXPORT_COMPLETED,
  NotificationType.ORDERS_EXPORT_FAILED,
  NotificationType.SHIPMENTS_EXPORT_STARTED,
  NotificationType.SHIPMENTS_EXPORT_COMPLETED,
  NotificationType.SHIPMENTS_EXPORT_FAILED,
  NotificationType.PRODUCT_IMPORT_COMPLETED,
  NotificationType.PRODUCT_ALIAS_IMPORT_COMPLETED,
  NotificationType.PRODUCT_BUNDLES_IMPORT_COMPLETED,
  NotificationType.PRODUCT_IMPORT_FAILED,
  NotificationType.PRODUCT_EXPORT_COMPLETED,
  NotificationType.PRODUCT_EXPORT_FAILED,
  NotificationType.INVENTORY_IMPORT_COMPLETED,
  NotificationType.INVENTORY_IMPORT_FAILED,
  NotificationType.INVENTORY_EXPORT_COMPLETED,
  NotificationType.INVENTORY_EXPORT_FAILED,
  NotificationType.LOCATIONS_EXPORT_STARTED,
  NotificationType.LOCATIONS_EXPORT_COMPLETED,
  NotificationType.LOCATIONS_EXPORT_FAILED,
  NotificationType.TOTES_EXPORT_STARTED,
  NotificationType.TOTES_EXPORT_COMPLETED,
  NotificationType.TOTES_EXPORT_FAILED,
  NotificationType.FORECASTING_EXPORT_FAILED,
  NotificationType.FULFILLMENTS_EXPORT_STARTED,
  NotificationType.FULFILLMENTS_EXPORT_COMPLETED,
  NotificationType.FULFILLMENTS_EXPORT_FAILED,
  NotificationType.TRANSACTION_HISTORY_EXPORT_STARTED,
  NotificationType.TRANSACTION_HISTORY_EXPORT_COMPLETED,
  NotificationType.TRANSACTION_HISTORY_EXPORT_FAILED,
  NotificationType.RETURNS_EXPORT_COMPLETED,
]);

const parseMetadata = (metadataJson) => {
  if (!metadataJson) {
    return null;
  }
  try {
    return JSON.parse(metadataJson);
  } catch {
    return null;
  }
};

const buildNotificationFromHubInfo = (hubInfo, notificationID, createdUtc) => ({
  notificationID,
  notificationType: hubInfo.notificationType,
  priority: hubInfo.priority,
  category: hubInfo.category,
  title: hubInfo.title,
  description: hubInfo.description,
  metadata: parseMetadata(hubInfo.metadataJson),
  isRead: false,
  isDismissed: false,
  createdUtc,
  readUtc: null,
  dismissedUtc: null,
  actions: hubInfo.actions,
});

export const persistNotificationEpic = (action$, { getState }) =>
  action$.pipe(
    ofType(PERSIST_NOTIFICATION),
    filter(() => {
      const featureFlags = new FeatureFlags({ state: getState() });
      return featureFlags.getFeatureFlag(FeatureFlagDeclarations.enableNotificationHub);
    }),
    mergeMap(({ hubInfo }) => {
      const isUserAction = USER_ACTION_NOTIFICATION_TYPES.has(hubInfo.notificationType);
      const cooldownHours = isUserAction ? null : AUTO_TRIGGER_COOLDOWN_HOURS;

      const apiCall = NotificationHubApiService.createNotification({
        notificationType: hubInfo.notificationType,
        priority: hubInfo.priority,
        category: hubInfo.category,
        title: hubInfo.title,
        description: hubInfo.description,
        metadataJson: hubInfo.metadataJson ?? null,
        cooldownHours,
      });

      return from(apiCall).pipe(
        mergeMap((response) => {
          if (response.isDuplicate) {
            return EMPTY;
          }

          const notification = buildNotificationFromHubInfo(hubInfo, response.notificationID, response.createdUtc);
          return of(addNotification(notification));
        }),
        // For user-initiated actions (imports, exports, batches), add a local-only
        // notification (notificationID: 0) so the hub still reflects what happened even
        // if the API is unreachable. Auto-triggered notifications are silently dropped.
        catchError(() => {
          if (isUserAction) {
            const fallback = buildNotificationFromHubInfo(hubInfo, 0, DateTime.now().toISO() ?? '');
            return of(addNotification(fallback));
          }
          return EMPTY;
        }),
      );
    }),
  );
